import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import './creditplate.css';

const inr = (n) => '₹' + Math.round(n).toLocaleString('en-IN');

const Reveal = ({ children, className = '' }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(
      ([e]) => e.isIntersecting && (el.classList.add('cp-in'), io.disconnect()),
      { threshold: 0.12 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return <div ref={ref} className={`cp-rv ${className}`}>{children}</div>;
};

const Counter = ({ to }) => {
  const [v, setV] = useState(0);
  useEffect(() => {
    let raf; let s = null;
    const f = (t) => {
      s = s || t;
      const p = Math.min((t - s) / 1800, 1);
      setV(Math.round(to * p));
      if (p < 1) raf = requestAnimationFrame(f);
    };
    raf = requestAnimationFrame(f);
    return () => cancelAnimationFrame(raf);
  }, [to]);
  return <>{v.toLocaleString('en-IN')}</>;
};

const WHY = [
  ['🤖', 'Instant Loan Approval', 'Hassle-free instant approvals powered by AI-based verification.'],
  ['🛡️', 'Secure & Trusted', 'Your personal and financial data is secured with industry-grade encryption.'],
  ['🚀', 'Fast Disbursal', 'Get funds credited to your account instantly after approval — no waiting.'],
  ['💎', 'Transparent Process', 'No hidden fees, no surprises, only honesty.'],
];
const FEATURES = [
  ['🔒', 'Secure & Compliant', 'Your data is protected with bank-grade encryption and strict regulatory compliance.'],
  ['📄', 'Transparent Terms', 'No hidden fees, no surprises. Understand every aspect of your loan upfront with clear documentation.'],
  ['⚡', 'Quick Approval', 'Get instant decisions and access funds within minutes, avoiding lengthy waiting periods.'],
  ['🔁', 'Flexible Repayment', 'Tailor your repayment schedule to your financial comfort and needs, easily managed online.'],
];
const STEPS = [
  ['Basic Application', 'Fill out the simple, 3-minute application form with your personal details.'],
  ['Document Submission', 'Securely upload necessary documents online for instant verification.'],
  ['Quick Approval', 'Receive instant decision and finalize the loan offer details.'],
  ['Instant Funds', 'Funds are immediately disbursed to your bank account.'],
];
const ELIGIBILITY = [
  [true, 'Age must be between 21 and 60 years old.'],
  [true, 'Must be a resident of India with valid ID proof (Aadhaar/PAN).'],
  [true, 'Salaried or Self-Employed with a stable source of income.'],
  [true, 'Minimum monthly net income of ₹15,000 or more.'],
  [false, 'We currently do not accept applicants with a default credit history.'],
];
const DOCS = {
  'Identity & Address Proof': ['Aadhaar Card', 'PAN Card', 'Selfie / Photograph', 'Utility Bill'],
  'Income & Employment': ['Latest Salary Slip', 'Employment Details', 'Income Proof'],
  'Bank Details & Others': ['Bank Statement', 'Bank Account Details', 'Other Supporting Documents'],
};
const TIPS = [
  ['📈', 'Monitor Your Credit Score', 'A good score (750+) ensures lower interest rates. Check it regularly and correct any errors.'],
  ['📅', 'Plan Repayment Early', 'Always know your EMI dates and schedule automatic payments to avoid late fees and penalties.'],
  ['🔍', 'Read the Fine Print', 'Thoroughly understand the terms, conditions, processing fees, and foreclosure charges before signing.'],
  ['⚖️', 'Compare Multiple Lenders', "Don't settle for the first offer. Compare interest rates and fees from different banks and platforms."],
  ['🎯', 'Borrow Only What You Need', 'Taking a bigger loan than necessary increases your debt burden and overall interest cost.'],
  ['📁', 'Keep Documentation Handy', 'Ensure all required documents are clear, current, and organized for a faster approval process.'],
];

const Calculator = () => {
  const [amount, setAmount] = useState(20000);
  const [rate, setRate] = useState(1);
  const [days, setDays] = useState(30);
  // Daily flat interest: Interest = Principal × (dailyRate% / 100) × days
  const interest = useMemo(() => amount * (rate / 100) * days, [amount, rate, days]);
  return (
    <div className="cp-calc">
      <div className="cp-card">
        <label>Loan Amount <span>{inr(amount)}</span></label>
        <input type="range" min="5000" max="100000" step="1000" value={amount} onChange={(e) => setAmount(+e.target.value)} />
        <label>Interest Rate <span>{rate}% / Day</span></label>
        <input type="range" min="0.9" max="1" step="0.01" value={rate} onChange={(e) => setRate(+e.target.value)} />
        <label>Loan Tenure <span>{days} Days</span></label>
        <input type="range" min="7" max="90" value={days} onChange={(e) => setDays(+e.target.value)} />
      </div>
      <div className="cp-res">
        <div><span>Repayment Amount</span><b>{inr(amount + interest)}</b></div>
        <div><span>Total Interest</span><b>{inr(interest)}</b></div>
        <div className="cp-tot"><span style={{ color: '#fff', fontSize: '1rem' }}>Total Payment</span><b>{inr(amount + interest)}</b></div>
        <div className="cp-dn"><i style={{ width: `${(amount / (amount + interest)) * 100}%` }} /></div>
        <small>Principal vs Interest · daily flat interest</small>
      </div>
    </div>
  );
};

const CreditPlateHome = () => {
  const [feat, setFeat] = useState(0);
  const [doc, setDoc] = useState(Object.keys(DOCS)[0]);

  useEffect(() => {
    const t = setInterval(() => setFeat((f) => (f + 1) % FEATURES.length), 4500);
    return () => clearInterval(t);
  }, [feat]);

  return (
    <main className="cp-home">
      {/* 1. Banner */}
      <section className="cp-hero" id="home">
        <div className="cp-bub" style={{ width: 220, height: 220, top: '12%', left: -60 }} />
        <div className="cp-bub" style={{ width: 120, height: 120, bottom: '14%', left: '40%', animationDelay: '2s' }} />
        <div className="cp-bub" style={{ width: 300, height: 300, top: -80, right: -80, animationDelay: '4s' }} />
        <div className="cp-wrap">
          <div>
            <span className="cp-tag">⚡ CreditPlate Instant Loan</span>
            <h1>Your Salary, Your <em>Financial Freedom</em></h1>
            <p>Experience seamless Ultra Short Term Loans with instant approval, zero hidden fees, and complete peace of mind. <b>Up to ₹1,00,000 Instantly.</b></p>
            <div className="cp-actions">
              <Link to="/apply-now" className="cp-btn">Apply Now</Link>
              <a href="#cp-calc" className="cp-btn cp-btn-ghost">Calculate Repayment</a>
            </div>
          </div>
          <div className="cp-pc">
            <small>Your Instant Limit</small>
            <div className="cp-am">₹<Counter to={100000} /></div>
            <div className="cp-bar"><i /></div>
            <small>Approval in 90 seconds · Funds in 30 mins</small>
            <div className="cp-chips"><span>🔒 Secure</span><span>⚡ Instant</span><span>📱 100% Online</span></div>
          </div>
        </div>
      </section>

      {/* 2. About */}
      <section className="cp-sec" id="cp-about">
        <Reveal className="cp-wrap cp-c">
          <span className="cp-eb">Instant Funds Relief ❤</span>
          <h2>Your <span>Financial Emergency,</span> Our Quick Solution</h2>
          <p className="cp-sub">When life hits you with an unexpected bill, you need speed, not paperwork. We provide fast, reliable Ultra Short Term Loans with minimum documentation and maximum efficiency.</p>
          <div className="cp-st">
            <div>⚡<b>90 Sec</b><small>Decision Time</small></div>
            <div>🔐<b>1 Hour</b><small>Fund Transfer Guarantee</small></div>
            <div>✅<b>No Stress</b><small>Credit Score Friendly</small></div>
          </div>
          <div className="cp-big"><b>₹1 Lakh</b><div>Maximum Instant Credit<br /><small>Funds transferred directly to your account within 30 minutes, after KYC verification.</small></div></div>
        </Reveal>
      </section>

      {/* 3. Why Choose Us */}
      <section className="cp-sec cp-alt">
        <Reveal className="cp-wrap cp-c">
          <span className="cp-eb">Why Choose Us</span>
          <h2>We Make <span>Your Financial Journey Effortless</span></h2>
          <div className="cp-grid">
            {WHY.map(([i, t, d]) => (
              <div className="cp-card" key={t}><div className="cp-ic">{i}</div><h3>{t}</h3><p>{d}</p></div>
            ))}
          </div>
        </Reveal>
      </section>

      {/* 4. Looking for a Loan */}
      <section className="cp-sec cp-ll">
        <div className="cp-wrap">
          <Reveal>
            <span className="cp-eb">Looking for a Loan?</span>
            <h2 style={{ color: '#fff' }}>Get <span>Instant Funds</span> in Minutes.</h2>
            <ul><li>No Hidden Fees</li><li>Instant Disbursement</li><li>100% Online Process</li><li>Secure &amp; Safe</li></ul>
            <Link to="/apply-now" className="cp-btn">Apply Instantly</Link>
          </Reveal>
          <Reveal>
            <div className="cp-ph">
              <b style={{ color: 'var(--cp-pr)' }}>CreditPlate</b> <small style={{ float: 'right' }}>🟢 Active</small>
              <p style={{ color: 'var(--cp-mu)', marginTop: 14, fontSize: '.85rem' }}>Your Instant Limit</p>
              <div style={{ fontSize: '2rem', fontWeight: 800 }}>₹1,00,000</div>
              <div className="cp-row"><span>Options</span><span>Flexible</span></div>
              <div className="cp-row"><span>Manage</span><span>Simple steps</span></div>
              <div className="cp-row"><span>Status</span><span>Ready to proceed</span></div>
              <Link to="/apply-now" className="cp-btn" style={{ width: '100%', textAlign: 'center', marginTop: 16 }}>Proceed Now</Link>
              <small style={{ display: 'block', textAlign: 'center', color: 'var(--cp-mu)', marginTop: 10 }}>All actions are safe &amp; simple</small>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 5. Loan Calculator */}
      <section className="cp-sec" id="cp-calc">
        <Reveal className="cp-wrap cp-c">
          <span className="cp-eb">Plan Smart</span>
          <h2>Smart <span>Loan</span> Calculator</h2>
          <Calculator />
        </Reveal>
      </section>

      {/* 6. Loan Features */}
      <section className="cp-sec cp-alt">
        <Reveal className="cp-wrap">
          <span className="cp-eb">Key Advantages</span>
          <h2>Experience <span>the Future</span> of Lending</h2>
          <div className="cp-ft">
            <div className="cp-tabs">
              {FEATURES.map((f, i) => (
                <button key={f[1]} className={i === feat ? 'cp-on' : ''} onClick={() => setFeat(i)}>{f[0]} {f[1]}</button>
              ))}
            </div>
            <div className="cp-card cp-show" key={feat}>
              <div className="cp-ic">{FEATURES[feat][0]}</div>
              <h3 style={{ fontSize: '1.5rem' }}>{FEATURES[feat][1]}</h3>
              <p style={{ margin: '8px 0 18px' }}>{FEATURES[feat][2]}</p>
              <Link to="/apply-now" className="cp-btn">Apply Now</Link>
            </div>
          </div>
        </Reveal>
      </section>

      {/* 7. Loan Steps */}
      <section className="cp-sec">
        <Reveal className="cp-wrap cp-c">
          <span className="cp-eb">4 Simple Steps</span>
          <h2>Your <span>Instant Loan</span> Process</h2>
          <div className="cp-sp">
            {STEPS.map(([t, d], i) => (
              <div className="cp-card" key={t}><div className="cp-n">{i + 1}</div><h3>{t}</h3><p>{d}</p></div>
            ))}
          </div>
        </Reveal>
      </section>

      {/* 8. Eligibility */}
      <section className="cp-sec cp-alt">
        <Reveal className="cp-wrap cp-two">
          <div>
            <span className="cp-eb">Quick Check</span>
            <h2>Are You <span>Eligible for Instant</span> Cash?</h2>
            <p className="cp-sub" style={{ marginBottom: 22 }}>Review the simple criteria below. If you meet these requirements, you're just minutes away from getting approved for your loan.</p>
            <Link to="/apply-now" className="cp-btn">Apply Now</Link>
          </div>
          <ul className="cp-el">
            {ELIGIBILITY.map(([ok, t]) => (
              <li key={t} className={ok ? '' : 'cp-x'}><i>{ok ? '✓' : '✕'}</i>{t}</li>
            ))}
          </ul>
        </Reveal>
      </section>

      {/* 9. Required Documents */}
      <section className="cp-sec">
        <Reveal className="cp-wrap cp-c">
          <span className="cp-eb">Digital Submission</span>
          <h2>Required <span>Documents</span></h2>
          <div className="cp-dt">
            {Object.keys(DOCS).map((k) => (
              <button key={k} className={k === doc ? 'cp-on' : ''} onClick={() => setDoc(k)}>{k}</button>
            ))}
          </div>
          <div className="cp-dl" key={doc}>
            {DOCS[doc].map((x, i) => <div key={x} style={{ animationDelay: `${i * 0.08}s` }}>📎 {x}</div>)}
          </div>
        </Reveal>
      </section>

      {/* 10. Loan Tips */}
      <section className="cp-sec cp-alt">
        <Reveal className="cp-wrap cp-c">
          <span className="cp-eb">Financial Wisdom</span>
          <h2>Smart Loan <span>Tips for Success</span></h2>
          <p className="cp-sub">Understanding the nuances of your loan agreement, from the Annual Percentage Rate (APR) to the tenure flexibility, is crucial. Our goal is to empower you with the right knowledge.</p>
          <div className="cp-kf"><div><b>98%</b>Approval Rate</div><div><b>5 Min</b>Disbursal Time</div><div><b>₹2 Cr+</b>Total Loans</div></div>
          <div className="cp-grid">
            {TIPS.map(([i, t, d]) => (
              <div className="cp-card" key={t}><div className="cp-ic">{i}</div><h3>{t}</h3><p>{d}</p></div>
            ))}
          </div>
        </Reveal>
      </section>
    </main>
  );
};

export default CreditPlateHome;
